import { useEffect, useRef, useState } from 'react'
import { api, type ExportVerdict, type FileVerdict, type PortfolioHit } from '../api'
import { useDesks } from '../desks'

/**
 * Rule export — TECHNICAL_DESIGN.md §5.6, §13, proposal launch gate 4.
 *
 * The artefact this product exists to produce. Everything else — the store, the
 * approvals, the review clocks — is what makes this file trustworthy; the file
 * is what the firm's compliance engine actually enforces.
 *
 * **The routes existed and had no screen**, which meant the one output a
 * customer needs was reachable only by somebody who could construct a URL. Found
 * when the question *where do I export the rules* was asked by an administrator
 * who could not find it, which is the correct way for that gap to surface and a
 * late one.
 *
 * **Nothing is pushed anywhere.** §10.3 wants every egress destination explicit
 * and enumerable for a firewall allowlist, and the way to satisfy that is to
 * have none: the appliance renders a file and a person carries it. So this
 * screen downloads, and offers no *send to* of any kind.
 *
 * **The digest is shown, not hidden.** It travels in a header on the download
 * and is the receiving side's integrity check — an export whose digest nobody
 * recorded is an export nobody can prove was not edited on the way.
 *
 * **Verification is two different questions**, and the screen keeps them apart.
 * *Check what would be exported* re-resolves and compares the object, catching a
 * dropped inherited rule — the failure that would let the engine enforce less
 * than the mandate while everything looked correct. *Check a file* reads back
 * what actually left, which is the only one of the two that sees a
 * serialisation bug. Neither is launch gate 4: that compares verdicts, and needs
 * something that evaluates holdings, which is the customer's engine and not this.
 */
export function Export({
  canExportPack,
  section,
}: {
  canExportPack: boolean
  /** Chosen from the main menu — see `App`. */
  section: 'rules' | 'verify' | 'pack'
}) {
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<PortfolioHit[]>([])
  const [chosen, setChosen] = useState<PortfolioHit | null>(null)
  /** One desk's funds, for somebody across the whole firm (decision 92). Everybody else
   *  exports their own desks' funds, which is all they can see. */
  const [desk, setDesk] = useState('')
  const desks = useDesks()
  const [format, setFormat] = useState<'json' | 'csv'>('json')
  const [digest, setDigest] = useState<string | null>(null)
  const [verdict, setVerdict] = useState<ExportVerdict | null>(null)
  const [fileVerdict, setFileVerdict] = useState<FileVerdict | null>(null)
  /** The name of the file being read, so the screen can say what it is doing
   *  and, afterwards, which file the verdict is about. */
  const [checking, setChecking] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const upload = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (query.trim().length < 2) {
      setHits([])
      return
    }
    api
      .findPortfolios(query)
      .then(setHits)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
  }, [query])

  const portfolioId = chosen?.id

  async function act(work: () => Promise<void>) {
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const scope = chosen
    ? chosen.code
    : desk
      ? `every fund on ${desk}`
      : desks.firmWide
        ? 'every portfolio'
        : `every fund on ${desks.desks.map((d) => d.code).join(', ') || 'your desks'}`
  /** The search offers the chosen desk's funds only. */
  const offered = desk ? hits.filter((h) => desks.fund(h.id) === desk) : hits

  return (
    <section>
      <header className="page-head">
        <h2>
          {section === 'rules'
            ? 'Export limits'
            : section === 'verify'
              ? 'Check a file'
              : 'Audit pack'}
        </h2>
        <p className="note">
          {section === 'rules'
            ? 'The limits as a file, for the system that checks portfolios against them. Nothing is sent anywhere: the file downloads to you, and you load it into your compliance system yourself.'
            : section === 'verify'
              ? 'Upload a file you exported earlier and this confirms it is still exactly what this system produced — useful after it has passed through another system, or before you rely on an older copy.'
              : 'A single file for an auditor or a client: a fund’s limits, who signed off on them and when, every change with the person who made it, and anything overdue for review.'}
        </p>
      </header>

      {error && <p className="error">{error}</p>}

      {section !== 'verify' && (
        <div className="card">
          <h3>Which funds</h3>
          {section === 'rules' && !desks.firmWide && desks.desks.length > 0 && (
            <p className="note" data-testid="export-desks">
              Your export covers {desks.desks.map((d) => `${d.code} · ${d.name}`).join(', ')}.
            </p>
          )}
          <div className="row-form">
            {section === 'rules' && desks.firmWide && desks.desks.length > 0 && (
              <label>
                Desk
                <select
                  value={desk}
                  data-testid="export-desk"
                  onChange={(e) => {
                    setDesk(e.target.value)
                    setChosen(null)
                    setQuery('')
                  }}
                >
                  <option value="">Every desk</option>
                  {desks.desks.map((d) => (
                    <option key={d.id} value={d.code}>
                      {d.code} · {d.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Fund
              <input
                value={chosen ? `${chosen.code} — ${chosen.name}` : query}
                placeholder={desk ? `Every fund on ${desk} — or search for one` : "Every fund — or search for one"}
                title="Leave it empty for every fund, or type a code to narrow it to one."
                onChange={(e) => {
                  setChosen(null)
                  setQuery(e.target.value)
                }}
              />
            </label>
            {section === 'rules' && (
              <label>
                Format
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as 'json' | 'csv')}
                >
                  <option value="json">JSON — recommended</option>
                  <option value="csv">CSV — if your system needs it</option>
                </select>
              </label>
            )}
          </div>

          {offered.length > 0 && !chosen && (
            <div className="candidates">
              {offered.map((hit) => (
                <button key={hit.id} className="ghost" onClick={() => setChosen(hit)}>
                  {hit.code} — {hit.name}
                </button>
              ))}
            </div>
          )}
          {chosen && (
            <p className="note">
              <strong>{chosen.code}</strong> only.{' '}
              <button
                className="ghost"
                onClick={() => {
                  setChosen(null)
                  setQuery('')
                }}
              >
                Every fund instead
              </button>
            </p>
          )}
        </div>
      )}

      {section === 'rules' && (<>
      <div className="card">
        <h3>The file</h3>

        <p className="note">
          Use JSON unless your system needs CSV. Both contain the same
          limits; CSV flattens the more detailed fields into single cells, so a
          little structure is harder to read there.
        </p>
        <p className="note">
          A limit that applies to a group of funds appears once for each fund
          in the group, marked with where it came from — so your system sees
          every limit that applies to a fund, wherever it was written.
        </p>

        <div className="actions">
          <button
            disabled={busy}
            onClick={() =>
              void act(async () => {
                setDigest(await api.downloadRuleExport(portfolioId, format, desk || undefined))
              })
            }
          >
            Export {scope}
          </button>
          <button
            className="ghost"
            disabled={busy}
            onClick={() =>
              void act(async () => {
                setFileVerdict(null)
                setVerdict(await api.verifyExport(portfolioId, desk || undefined))
              })
            }
          >
            Check what would be exported
          </button>
        </div>

        {digest && (
          <div className="callout">
            <h3>Downloaded</h3>
            <p className="note">
              Keep this reference number with the file. It is how you or we can
              later confirm the file is exactly the one this system produced and
              has not been altered.
            </p>
            <p className="secret">
              <code>{digest}</code>
            </p>
          </div>
        )}

        {verdict && (
          <div className={verdict.clean ? 'callout' : 'warning'}>
            <h3>{verdict.clean ? 'Matches the limits in GUARD' : 'Discrepancies found'}</h3>
            <p>
              <span className="limit">{verdict.executable}</span> rule(s) your
              engine can execute,{' '}
              <span className="limit">{verdict.not_executable}</span> it cannot,{' '}
              <span className="limit">{verdict.undetermined}</span> undetermined.
            </p>
            <p className="note">
              Some limits are written in words rather than as a number — a
              restriction on a named issuer, for example. They are exported as text
              and marked as such, so your compliance team applies them alongside the
              rules your system executes.
            </p>
            {verdict.discrepancies.length > 0 && (
              <ul className="hits">
                {verdict.discrepancies.map((d, i) => (
                  <li key={i} className="refused">
                    {d.portfolio_code} · {d.rule} — {d.detail}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      </>)}

      {section === 'verify' && (
      <div className="card">
        <h3>Check a file you already have</h3>
        <p className="note">
          Upload a file you exported earlier and this confirms whether it is
          still exactly what this system produced. Useful after it has passed
          through another system, or before you rely on an older copy.
        </p>
        <div className="actions">
          <input
            ref={upload}
            type="file"
            accept=".json,.csv,application/json,text/csv"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (!file) return
              setChecking(file.name)
              void act(async () => {
                setVerdict(null)
                setFileVerdict(null)
                setFileVerdict(await api.verifyExportFile(file))
              })
            }}
          />
          {/* Something happens on selection. There is no button here — choosing
              the file *is* the action — so without this the screen sat still
              for the length of an upload and a re-resolve, which is exactly as
              long as a screen that has done nothing. */}
          {busy && checking && (
            <span className="note" data-testid="checking">
              Reading {checking}…
            </span>
          )}
        </div>

        {fileVerdict && (
          <div className={fileVerdict.faithful ? 'callout' : 'warning'}>
            <h3>
              {fileVerdict.faithful
                ? `${checking ?? 'That file'} is what we produced`
                : `${checking ?? 'That file'} is not what we produced`}
            </h3>
            <p>
              {fileVerdict.rules_read} rule(s) read ·{' '}
              {fileVerdict.digest_matches === null
                ? 'no digest to check'
                : fileVerdict.digest_matches
                  ? 'digest matches'
                  : 'digest does not match'}
            </p>
            {/* The formats do not support the same checks, and a verdict that
                does not say which ran is a verdict about an unknown claim. The
                digest is what catches an altered *value*; the CSV has nowhere
                to carry one, so a clean result there means the right rules are
                present for the right funds and nothing more. */}
            {fileVerdict.digest_matches === null && (
              <p className="note" data-testid="no-digest">
                This confirms the right limits are present for the right funds. A
                CSV’s digest travelled in a header when you downloaded it; to confirm
                every value is unchanged as well, check the JSON export, which
                carries its digest inside the file.
              </p>
            )}
            {fileVerdict.discrepancies.length > 0 && (
              <ul className="hits">
                {fileVerdict.discrepancies.map((d, i) => (
                  <li key={i} className="refused">
                    {d.portfolio_code} · {d.rule} — {d.detail}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      )}

      {section === 'pack' && canExportPack && (
        <div className="card">
          <h3>What the pack contains</h3>
          <p className="note">
            A single file for an auditor or a client: the fund’s limits, who
            signed off on them and when, every change with the person who made
            it, and anything that was overdue for review.
          </p>
          <div className="actions">
            <button
              className="ghost"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await api.downloadAuditPack(portfolioId)
                })
              }
            >
              Produce the pack for {scope}
            </button>
          </div>
        </div>
      )}

      {section === 'rules' && (
      <div className="card">
        <h3>What to do with the file</h3>
        <p className="note">
          Two ways firms use it, and you may well do both.
        </p>
        <dl className="roles">
          <div>
            <dt>Load it into your OMS</dt>
            <dd>
              Aladdin, Charles River, Bloomberg AIM, SimCorp and the rest each
              take a rule import in their own format. Use JSON unless yours needs
              CSV, and map the fields once against their import specification —
              after that it is the same file every time. Your system then tests
              holdings against these limits, which is the part that happens
              there rather than here.
            </dd>
          </div>
          <div>
            <dt>Use it for a review or an audit</dt>
            <dd>
              Every line carries the fund, the limit, and the document and clause
              it came from — so a reviewer can take any row back to the paragraph
              it was written from. That is usually what an auditor is asking for
              when they ask how you know what your limits are.
            </dd>
          </div>
        </dl>
        <p className="note">
          Limits recorded as text — a restriction on a named issuer, a rating
          methodology — are in the file and marked as text. No system can test
          those automatically; they need a person, and having them in the same
          file is what stops them being forgotten.
        </p>
      </div>
      )}
    </section>
  )
}
